import React from 'react'
import { useTranslation } from 'react-i18next'
import { Ban, Shuffle } from 'lucide-react'
import { CANVAS_NOTE_HUES, getCanvasNoteShade, type CanvasNoteColorEntry, type CanvasNoteColorKey } from '../utils/canvasNoteStyle'
import { CanvasNoteDrop } from './CanvasNoteDrop'
import type { CanvasOptionsBarColorKind, CanvasOptionsBarPlacement } from './CanvasOptionsBar.types'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'

export interface CanvasColorPickerProps {
  /** Die aktuelle Farbe — `null`, wenn die Auswahl uneinheitlich ist. */
  value: string | null
  /**
   * `ink`: der Wähler eines **Texts** — vorn steht zusätzlich der Tropfen der
   * **Standard**-Tinte. `highlight`: der **Marker** des Texts — vorn steht
   * „kein Marker". Ohne Angabe `surface`.
   */
  kind?: CanvasOptionsBarColorKind | 'highlight'
  /** Ob der vordere Eintrag gewählt ist — Standard-Tinte bzw. kein Marker. */
  defaultSelected?: boolean
  /** Der vordere Eintrag gewählt (nur bei `kind: 'ink'` und `'highlight'`). */
  onPickDefault?: () => void
  /**
   * „Marker wechseln" ganz rechts, hinter einem Trenner (nur bei `kind:
   * 'highlight'`): Die Marker bekommen neue Formen. Der Wähler bleibt dabei
   * offen — man mischt, bis es gefällt (gewünscht 2026-09-23).
   */
  onShuffle?: () => void
  /** Ob es etwas zu mischen gibt — ohne Marker im Text ist der Knopf tot. */
  shuffleDisabled?: boolean
  placement: CanvasOptionsBarPlacement
  /**
   * Der Aufklapper wird VON AUSSEN gemessen — die Leiste prüft, ob er in die
   * Richtung passt, in die sie zeigt. Ab React 19 genügt dafür eine schlichte
   * Prop, kein `forwardRef`.
   */
  ref?: React.Ref<HTMLDivElement>
  onPick: (key: CanvasNoteColorKey) => void
  /**
   * Der Zeiger steht auf einem Tropfen (`{ color }`, `color: null` = vorderer
   * Eintrag) oder hat die Reihe verlassen (`null`). Zwischen zwei Tropfen
   * kommt KEIN `null` — sonst flackerte das Element auf dem Weg von der hellen
   * zur dunklen Stufe. Das Schließen meldet die Leiste, nicht der Wähler.
   */
  onPreview?: (choice: { color: string | null } | null) => void
  /** Blendet gerade ab (siehe `usePresence`) — als `data-leaving`, die SCSS spielt rückwärts. */
  leaving?: boolean
}

/**
 * Der **Farbwähler** — die elf Farben in einer Reihe, 8px über (oder unter)
 * der **Options-Leiste**, zentriert über seinem Knopf (Figma `5083:51492`).
 *
 * **Jede Farbe in zwei Stufen, übereinander.** In der Reihe steht die helle;
 * beim Überfahren geht darüber ein zweiter Tropfen auf, dieselbe Farbe nur
 * dunkler. Unten wählt man hell, oben dunkel — wie in FigJam. Die Gruppe aus
 * beiden (`__swatch-pair`) trägt den Hover, damit der helle Tropfen gehoben
 * bleibt, während der Zeiger nach oben zum dunklen wandert; das Wachsen und
 * Erscheinen ist reine SCSS (siehe `CanvasOptionsBar.scss`).
 *
 * **In der Reihe steht, was gewählt ist.** Ist die dunkle Stufe gewählt,
 * zeigt die Reihe den dunklen Tropfen mit seinem Ring, und beim Überfahren
 * geht darüber der HELLE auf — die Paare tauschen für diese eine Farbe die
 * Plätze. So trägt die gewählte Farbe immer ihren Ring in der Reihe, und in
 * Ruhe steht nie ein zweiter Tropfen über dem Wähler. Die gewählte Gruppe
 * hebt sich beim Überfahren wie jede andere.
 *
 * **Seine Farben sind kräftiger als die der Zettel** (`swatch` statt `hex`):
 * Auf 16px Tropfengröße wären die Flächentöne nicht auseinanderzuhalten. Man
 * wählt also ein deutlicheres Abbild dessen, was man bekommt — dieselbe
 * Entscheidung wie im Entwurf.
 *
 * **Er trägt den Ton seiner Leiste** (`--bar-*`, geerbt), ist also dunkel und
 * farbadaptiv wie sie — genau wie gezeichnet. Auf dunklem Grund tragen die
 * neun Pastelltöne am besten, und der Wähler liest sich als Fortsetzung der
 * Leiste, aus der er kommt. Das **Schriftmenü** bleibt daneben ein helles
 * App-Popover: Es zeigt Text, keine Farben, und dunkel auf hell liest sich
 * dünne Handschrift besser.
 *
 * **Keine Tooltips an den Tropfen:** Beim Überfahren zeigt das Element die
 * Farbe schon (`onPreview`) — die erklärt sich dort besser als ein Name.
 * `aria-label` bleibt für Screenreader.
 */
export const CanvasColorPicker: React.FC<CanvasColorPickerProps> = ({ value, kind = 'surface', defaultSelected = false, onPickDefault, onShuffle, shuffleDisabled = false, placement, onPick, onPreview, ref, leaving = false }) => {
  const { t } = useTranslation()

  return (
    <div
      ref={ref}
      className={`canvas-options-bar__flyout canvas-options-bar__flyout--dark canvas-options-bar__flyout--center canvas-options-bar__flyout--${placement}`}
      data-leaving={leaving ? '' : undefined}
      role="listbox"
      aria-label={t(kind === 'highlight' ? 'projectCanvas.text.highlight' : 'projectCanvas.note.changeColor')}
    >
      <div className="canvas-options-bar__swatches" onMouseLeave={() => onPreview?.(null)}>
        {/*
          Der **Marker** hat vorn „keinen" — derselbe Platz wie die Standard-Tinte,
          aber ein gewöhnlicher Knopf wie „Marker wechseln", kein Tropfen: Er
          hebt sich beim Überfahren nicht (gewünscht 2026-09-26).
        */}
        {kind === 'highlight' && (
          <button
            aria-label={t('projectCanvas.text.highlightNone')}
            type="button"
            role="option"
            aria-selected={defaultSelected}
            className="canvas-options-bar__button canvas-options-bar__button--flyout canvas-options-bar__button--none"
            onMouseEnter={() => onPreview?.({ color: null })}
            onClick={() => onPickDefault?.()}
          >
            <Ban size={16} strokeWidth={2} />
          </button>
        )}
        {/*
          Die **Standard**-Tinte des Texts: dunkel auf hellem, hell auf dunklem
          Grund — der Tropfen zeigt beides, halb und halb. Ein Paar ohne zweite
          Stufe, damit er in der Reihe genauso steht wie die anderen.
        */}
        {kind === 'ink' && (
          <div className={`canvas-options-bar__swatch-pair${defaultSelected ? ' is-active' : ''}`}>
            <button
              aria-label={t('projectCanvas.text.colorDefault')}
              type="button"
              role="option"
              aria-selected={defaultSelected}
              className={`canvas-options-bar__swatch${defaultSelected ? ' is-active' : ''}`}
              style={{ '--swatch': '#0a0a0a', '--swatch-border': '#525252' } as React.CSSProperties}
              onMouseEnter={() => onPreview?.({ color: null })}
              onClick={() => onPickDefault?.()}
            >
              <CanvasNoteDrop
                fill={['#0a0a0a', '#fafafa']}
                size={18}
                outline={defaultSelected ? 'var(--bar-chip-border)' : undefined}
              />
            </button>
          </div>
        )}
        {CANVAS_NOTE_HUES.map(light => {
          const dark = getCanvasNoteShade(light.key, 'dark')
          const darkActive = dark.key === value
          const active = light.key === value || darkActive
          // In der Reihe die gewählte Stufe, darüber die andere.
          const row = darkActive ? dark : light
          const above = darkActive ? light : dark
          const name = t(`projectCanvas.note.color.${light.key}`)
          const title = (color: CanvasNoteColorEntry) =>
            color.shade === 'dark' ? t('projectCanvas.note.colorDark', { color: name }) : name
          return (
            <div
              key={light.key}
              className={`canvas-options-bar__swatch-pair${active ? ' is-active' : ''}`}
            >
              <button
                aria-label={title(row)}
                type="button"
                role="option"
                aria-selected={active}
                className={`canvas-options-bar__swatch${active ? ' is-active' : ''}`}
                style={swatchVars(row)}
                onMouseEnter={() => onPreview?.({ color: row.key })}
                onClick={() => onPick(row.key)}
              >
                {/*
                  Ohne Kontur, außer bei der GEWÄHLTEN: Die trägt ihre Markierung
                  in Tropfenform — ein Ring mit transparenter Luft, vom Tropfen
                  selbst gezeichnet, im hellen Ton der Familie
                  (`--bar-chip-border`). Ein runder Ring am Knopf passte nicht
                  zur Form.
                */}
                <CanvasNoteDrop
                  fill={row.swatch}
                  size={18}
                  outline={active ? 'var(--bar-chip-border)' : undefined}
                />
              </button>
              <button
                aria-label={title(above)}
                type="button"
                role="option"
                aria-selected={false}
                className="canvas-options-bar__swatch-shade"
                style={swatchVars(above)}
                onMouseEnter={() => onPreview?.({ color: above.key })}
                onClick={() => onPick(above.key)}
              >
                <CanvasNoteDrop fill={above.swatch} size={18} />
              </button>
            </div>
          )
        })}
        {kind === 'highlight' && onShuffle && (
          <>
            <span className="canvas-options-bar__divider canvas-options-bar__divider--flyout" aria-hidden="true" />
            {/*
              Kein Tropfen: Er liegt in derselben Reihe, zeigt aber keine Farbe —
              auf ihm gilt wieder die eingestellte (gemeldet 2026-09-24). Am
              Tooltip und nicht am Knopf: Deaktiviert meldet der Knopf kein
              Überfahren, die Hülle darum schon (siehe `WithTooltip`).
            */}
            <WithTooltip content={t('projectCanvas.text.highlightShuffle')} onMouseEnter={() => onPreview?.(null)}>
              <button
                type="button"
                aria-label={t('projectCanvas.text.highlightShuffle')}
                className="canvas-options-bar__button canvas-options-bar__button--flyout"
                disabled={shuffleDisabled}
                onClick={() => onShuffle()}
              >
                <Shuffle size={16} strokeWidth={2} />
              </button>
            </WithTooltip>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Die eigene Farbe als Variable: Der Rand des Tropfens ist derselbe Ton, nur
 * dunkler — gerechnet in der SCSS (`oklch(from …)`). Weiß und Grau haben
 * keinen Ton, aus dem sich etwas rechnen ließe; die Rechnung lieferte dort
 * einen Stich. Sie bekommen den Rand direkt: Weiß ein Hellgrau (ein dunkler
 * Rand machte aus dem weißen Tropfen einen Ring), seine dunkle Stufe eine
 * Spur tiefer, Grau ein Dunkelgrau.
 */
function swatchVars(color: CanvasNoteColorEntry): React.CSSProperties {
  const border = color.family === 'neutral'
    ? (color.shade === 'dark' ? '#a3a3a3' : '#d4d4d4')
    : color.family === 'zinc' ? '#525252' : undefined
  return {
    '--swatch': color.swatch,
    ...(border ? { '--swatch-border': border } : {}),
  } as React.CSSProperties
}
